<template>
  <div>
    <div class="flex justify-between mt-5">
      <div class="flex justify-between gap-x-3">
        <p class="text-lg leading-none text-gray-500">Delivery Intent</p>
        <span v-if="isToday" class="px-2 py-1 text-xs leading-none text-gray-600 uppercase bg-green-200 rounded-sm font-catamaran">Today</span>
        <span v-else class="px-2 py-1 text-xs leading-none text-gray-600 uppercase bg-red-200 rounded-sm font-catamaran">Tomorrow</span>
      </div>
      <router-link class="text-blue-500" :to="{name: 'DeliveryIntents'}">View All</router-link>
    </div>

    <div class="flex flex-col p-3 mt-2 bg-white rounded-2xl shadow-sm">
      <div class="grid grid-cols-3 gap-2 text-lg text-center text-gray-600">
        <div class="text-left">Count</div>
        <div>1 Litre</div>
        <div>500 ML</div>
      </div>

      <div class="grid grid-cols-3 gap-2 items-center text-center">
        <div class="text-gray-500 text-left">Delivery</div>
        <div class="text-2xl">{{ deliveries.count_deliveries_1l }}</div>
        <div class="text-2xl">{{ deliveries.count_deliveries_500ml }}</div>
      </div>

      <div class="grid grid-cols-3 gap-2 items-center text-center">
        <div class="text-gray-500 text-left">Sample</div>
        <div class="text-2xl">{{ deliveries.count_samples_1l }}</div>
        <div class="text-2xl">{{ deliveries.count_samples_500ml }}</div>
      </div>

      <div class="grid grid-cols-3 gap-2 items-center text-center">
        <div class="text-gray-500 text-left">Manual</div>
        <div class="text-2xl" v-if="!isEditingManual">{{ manual.bottle_1000ml }}</div>

        <div v-if="isEditingManual" class="flex items-center text-2xl border rounded-lg overflow-hidden">
          <button class="px-2 bg-gray-100" @click="decreaseBottle1L">-</button>
          <input v-model.number="manual.bottle_1000ml" class="w-full text-center outline-none" type="number">
          <button class="px-2 bg-gray-100" @click="manual.bottle_1000ml++">+</button>
        </div>

        <div class="text-2xl" v-if="!isEditingManual">{{ manual.bottle_500ml }}</div>

        <div v-if="isEditingManual" class="flex items-center text-2xl border rounded-lg overflow-hidden">
          <button class="px-2 bg-gray-100" @click="decreaseBottle500ml">-</button>
          <input v-model.number="manual.bottle_500ml" class="w-full text-center outline-none" type="number">
          <button class="px-2 bg-gray-100" @click="manual.bottle_500ml++">+</button>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-2 items-center text-center font-semibold">
        <div class="text-left text-gray-600">Total</div>
        <div class="text-2xl">{{ totalBottle1L }}</div>
        <div class="text-2xl">{{ totalBottle500ml }}</div>
      </div>

      <div v-if="editManual || isEditingManual" class="mt-2 text-center flex justify-center gap-3">
        <button v-if="!isEditingManual && editManual" @click="startEditManual" class="px-3 py-1 text-sm text-gray-600 border uppercase rounded-full bg-slate-300">Edit</button>
        <div v-if="isEditingManual" class="flex gap-3">
          <button @click="saveManual" :disabled="isSaveManual" class="px-3 py-1 text-sm text-gray-600 border uppercase rounded-full bg-slate-300">Save Manual</button>
          <button @click="cancelManual" class="px-3 py-1 text-sm text-gray-500 border uppercase rounded-full bg-slate-300">Cancel</button>
        </div>
        <button v-if="!isEditingManual && editManual" @click="save" :class="['px-3 py-1 text-sm uppercase rounded-full', isIntentChanged ? 'bg-blue-500 text-white' : 'bg-slate-300 text-gray-600']">{{ isIntentChanged ? 'Update Intent' : 'Updated' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import mixin from "@/utils/mixin.js";
import formMixin from "@/utils/mixins/form.js";

export default {
  name: 'DashboardDeliveryIntent',
  mixins: [ mixin, formMixin ],

  data() {
    return {
      isEditingManual: false,
      manual: { bottle_1000ml: 0, bottle_500ml: 0 },
      originalManual: {},
      isSaveManual:false,
      savedIntent: { bottle_1000ml: 0, bottle_500ml: 0 }
    }
  },

  props: {
    intent: { type: Object, required: false },
    manualCount: { type: Object, required: false },
    editManual: { type: Boolean, required: false },
    deliveries: { type: Object, required: false }
  },

  computed: {
    isToday() {
      return dayjs(new Date()).diff(this.intent.date, 'day', true) > 0;
    },

    totalBottle1L() {
      return (Number(this.deliveries.count_deliveries_1l) + Number(this.deliveries.count_samples_1l) + Number(this.manual.bottle_1000ml));
    },

    totalBottle500ml() {
      return (Number(this.deliveries.count_deliveries_500ml) + Number(this.deliveries.count_samples_500ml) + Number(this.manual.bottle_500ml));
    },

    isIntentChanged() {
      return (Number(this.totalBottle1L) !== Number(this.savedIntent.bottle_1000ml) || Number(this.totalBottle500ml) !== Number(this.savedIntent.bottle_500ml));
    }
  },

  methods: {
    decreaseBottle1L() {
      this.manual.bottle_1000ml--;
    },

    decreaseBottle500ml() {
      this.manual.bottle_500ml--;
    },

    startEditManual() {
      this.originalManual = { ...this.manual }
      this.isEditingManual = true
    },

    cancelManual() {
      this.manual = { ...this.originalManual }
      this.isEditingManual = false
    },

    saveManual() {
      if (this.isSaveManual) return;
      this.isSaveManual = true;
      this.$parent.state = 'loading';

      this.request('POST', 'delivery-intents/update', this.manual, (resp) => {
        this.$parent.state = 'idle';
        
        if (resp.success) {
          this.isEditingManual = false
          this.$parent.data.user.manual_count.bottle_1000ml = this.manual.bottle_1000ml;
          this.$parent.data.user.manual_count.bottle_500ml = this.manual.bottle_500ml;
        }
        this.isSaveManual=false;
        alert(resp.message)
      })
    },

    save() {
      this.intent.bottle_1000ml = this.totalBottle1L;
      this.intent.bottle_500ml = this.totalBottle500ml;

      let validationRules = {
        bottle_1000ml: 'required|numeric',
        bottle_500ml: 'required|numeric',
        raw_milk: 'required|numeric',
      };

      if (!this.validateForm(this.intent, validationRules)) return;

      this.$parent.state = 'loading';

      this.request('POST', 'delivery-intents', this.intent, (resp) => {
        this.$parent.state = 'idle';

        if(resp.success) {
          this.$parent.data.delivery_intent.bottle_1000ml = this.intent.bottle_1000ml;
          this.$parent.data.delivery_intent.bottle_500ml = this.intent.bottle_500ml;
          this.savedIntent.bottle_1000ml = this.intent.bottle_1000ml;
          this.savedIntent.bottle_500ml = this.intent.bottle_500ml;
        }
        alert(resp.message);
      });
    }
  },

  mounted() {
    if (this.manualCount) {
      this.manual = {
        bottle_1000ml: Number(this.manualCount.bottle_1000ml),
        bottle_500ml: Number(this.manualCount.bottle_500ml)
      };
    }

    this.savedIntent = {
      bottle_1000ml: Number(this.intent.bottle_1000ml),
      bottle_500ml: Number(this.intent.bottle_500ml)
    };
  },
}
</script>
